import React, { useMemo, useState } from 'react'
import { Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js'
import { PerformancePeriodData } from '../../services/action_summary_api'
import { getChartColor } from '../../utils/chartColors'
import { formatCurrency } from '../../utils/formatting'
import { Tabs } from '../FbUI/Tabs'

// Register Chart.js components (required before using Bar chart)
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

/** Chart color indices from chartColors.ts palette. */
const CHART_COLORS = {
  DEALS_OPENED: 0, // Dark Green
  DEALS_WON: 1, // Light Green
  DEALS_LOST: 6, // Red
  EMAILS_SENT: 7, // Cyan
  VALUE_OPENED: 3, // Blue
  VALUE_WON: 1, // Light Green
} as const

/** Available tabs for the performance chart. */
type ChartTab = 'deals' | 'emails' | 'value'

interface PerformanceChartProps {
  /** Array of period data to display, ordered newest first from API. */
  periods: PerformancePeriodData[]
  /** Optional additional CSS class names. */
  className?: string
}

/**
 * Grouped bar chart showing performance metrics over time.
 *
 * Features:
 * - Tabs to switch between Deals, Emails, and Value views
 * - Grouped bar chart for each category
 * - Tooltips show relevant context (win rate for deals, etc.)
 *
 * Color scheme:
 * - Deals opened: Dark Green (primary metric)
 * - Deals won: Light Green (success indicator)
 * - Deals lost: Red (failure indicator)
 * - Emails sent: Cyan
 * - Value opened: Blue
 * - Value won: Light Green
 */
export const PerformanceChart: React.FC<PerformanceChartProps> = ({
  periods,
  className,
}) => {
  const [activeTab, setActiveTab] = useState<ChartTab>('deals')
  const tabOptions = useMemo(
    () => [
      { key: 'deals' as ChartTab, title: 'Deals' },
      { key: 'emails' as ChartTab, title: 'Emails' },
      { key: 'value' as ChartTab, title: 'Value' },
    ],
    []
  )

  // Reverse to show oldest to newest (left to right)
  const sortedPeriods = useMemo(() => [...periods].reverse(), [periods])
  const labels = useMemo(
    () => sortedPeriods.map((p) => p.period_label),
    [sortedPeriods]
  )

  const dealsChartData = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: 'Deals Opened',
          data: sortedPeriods.map((p) => p.deals_opened),
          backgroundColor: getChartColor(CHART_COLORS.DEALS_OPENED),
          borderRadius: 3,
        },
        {
          label: 'Deals Won',
          data: sortedPeriods.map((p) => p.deals_won),
          backgroundColor: getChartColor(CHART_COLORS.DEALS_WON),
          borderRadius: 3,
        },
        {
          label: 'Deals Lost',
          data: sortedPeriods.map((p) => p.deals_lost),
          backgroundColor: getChartColor(CHART_COLORS.DEALS_LOST),
          borderRadius: 3,
        },
      ],
    }),
    [labels, sortedPeriods]
  )

  const emailsChartData = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: 'Emails Sent',
          data: sortedPeriods.map((p) => p.emails_sent),
          backgroundColor: getChartColor(CHART_COLORS.EMAILS_SENT),
          borderRadius: 3,
        },
      ],
    }),
    [labels, sortedPeriods]
  )

  const valueChartData = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: 'Value Opened',
          data: sortedPeriods.map((p) => p.value_opened),
          backgroundColor: getChartColor(CHART_COLORS.VALUE_OPENED),
          borderRadius: 3,
        },
        {
          label: 'Value Won',
          data: sortedPeriods.map((p) => p.value_won),
          backgroundColor: getChartColor(CHART_COLORS.VALUE_WON),
          borderRadius: 3,
        },
      ],
    }),
    [labels, sortedPeriods]
  )

  const getChartData = () => {
    switch (activeTab) {
      case 'deals':
        return dealsChartData
      case 'emails':
        return emailsChartData
      case 'value':
        return valueChartData
    }
  }

  const baseOptions: ChartOptions<'bar'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: true,
            boxWidth: 8,
            padding: 16,
            font: {
              size: 11,
            },
          },
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          padding: 12,
          titleFont: {
            size: 12,
          },
          bodyFont: {
            size: 11,
          },
          callbacks:
            activeTab === 'deals'
              ? {
                  afterBody: (tooltipItems) => {
                    const periodIndex = tooltipItems[0]?.dataIndex
                    if (periodIndex !== undefined) {
                      const period = sortedPeriods[periodIndex]
                      if (period?.win_rate !== null) {
                        return `\nWin Rate: ${period.win_rate}%`
                      }
                    }
                    return ''
                  },
                }
              : activeTab === 'value'
                ? {
                    label: (context) => {
                      const value = context.raw as number
                      return `${context.dataset.label}: ${formatCurrency(value)}`
                    },
                  }
                : undefined,
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            font: {
              size: 10,
            },
          },
        },
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text:
              activeTab === 'deals'
                ? 'Deals'
                : activeTab === 'emails'
                  ? 'Emails'
                  : 'Value ($)',
            font: {
              size: 10,
            },
          },
          ticks: {
            font: {
              size: 10,
            },
            precision: 0,
            callback:
              activeTab === 'value'
                ? (value) => formatCurrency(value as number)
                : undefined,
          },
          grid: {
            color: 'rgba(0, 0, 0, 0.05)',
          },
        },
      },
    }),
    [activeTab, sortedPeriods]
  )

  if (periods.length === 0) {
    return (
      <div className={className}>
        <div className="flex items-center justify-center h-48 text-gray-500 text-sm">
          No data available for the selected period
        </div>
      </div>
    )
  }

  return (
    <div className={className}>
      {/* Tab buttons */}
      <div className="mb-3">
        <Tabs<ChartTab>
          tabs={tabOptions}
          selectedTab={activeTab}
          setSelectedTab={setActiveTab}
          isSmall
          noGap
        />
      </div>

      {/* Chart */}
      <div className="h-56">
        <Bar data={getChartData()} options={baseOptions} />
      </div>
    </div>
  )
}
